// Google OAuth Service for Frontend
// This service handles Google Sign-In integration using Google Identity Services

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleInitConfig) => void;
          prompt: () => void;
          renderButton: (element: HTMLElement, config: GoogleButtonConfig) => void;
          disableAutoSelect: () => void;
        };
      };
    };
  }
}

interface GoogleInitConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleButtonConfig {
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: string;
}

interface GoogleCredentialResponse {
  credential: string;
  select_by?: string;
}

export class GoogleAuthService {
  private static instance: GoogleAuthService;
  private isInitialized = false;
  private readonly clientId: string;

  private constructor() {
    // SECURITY: Only use environment variable, no hardcoded fallback
    this.clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
    
    if (this.clientId) {
      console.log('GoogleAuthService initialized with client ID:', this.clientId.substring(0, 20) + '...');
    } else {
      console.warn('⚠️ VITE_GOOGLE_CLIENT_ID not set - Google OAuth will not work');
    }
  }

  public static getInstance(): GoogleAuthService {
    if (!GoogleAuthService.instance) {
      GoogleAuthService.instance = new GoogleAuthService();
    }
    return GoogleAuthService.instance;
  }

  /**
   * Load Google Identity Services script and initialize
   */
  public async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    // Check if Google Identity Services is already loaded
    if (window.google?.accounts?.id) {
      this.initializeGoogleAuth();
      return;
    }

    // Load Google Identity Services script
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        try {
          this.initializeGoogleAuth();
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      
      script.onerror = () => {
        reject(new Error('Failed to load Google Identity Services'));
      };
      
      document.head.appendChild(script);
    });
  }

  /**
   * Initialize Google Auth with configuration
   */
  private initializeGoogleAuth(): void {
    if (!window.google?.accounts?.id) {
      throw new Error('Google Identity Services not loaded');
    }

    try {
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: this.handleCredentialResponse.bind(this),
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      this.isInitialized = true;
      console.log('Google Auth initialized successfully');
    } catch (error) {
      console.error('Error initializing Google Auth:', error);
      throw error;
    }
  }

  /**
   * Handle Google credential response
   */
  private handleCredentialResponse(response: GoogleCredentialResponse): void {
    // This will be called by the Google Sign-In button
    // The actual handling is done by the component that calls signIn()
    console.log('Google credential response received:', response);
  }

  /**
   * Sign in with Google - returns a promise with the credential
   */
  public async signIn(): Promise<string> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    return new Promise((resolve, reject) => {
      if (!window.google?.accounts?.id) {
        reject(new Error('Google Identity Services not initialized'));
        return;
      }

      // Create a temporary callback for this sign-in attempt
      const originalCallback = this.handleCredentialResponse;
      
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: (response: GoogleCredentialResponse) => {
          // Restore original callback
          this.handleCredentialResponse = originalCallback;
          resolve(response.credential);
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Trigger the sign-in prompt
      window.google.accounts.id.prompt();
    });
  }

  /**
   * Render Google Sign-In button
   */
  public renderButton(
    element: HTMLElement, 
    config: GoogleButtonConfig = {},
    onSuccess: (credential: string) => void,
    onError: (error: Error) => void
  ): void {
    if (!this.isInitialized) {
      this.initialize().then(() => {
        this.renderButtonInternal(element, config, onSuccess, onError);
      }).catch(onError);
      return;
    }

    this.renderButtonInternal(element, config, onSuccess, onError);
  }

  /**
   * Internal method to render the button
   */
  private renderButtonInternal(
    element: HTMLElement,
    config: GoogleButtonConfig,
    onSuccess: (credential: string) => void,
    onError: (error: Error) => void
  ): void {
    if (!window.google?.accounts?.id) {
      onError(new Error('Google Identity Services not initialized'));
      return;
    }

    try {
      // Set up callback for this button
      window.google.accounts.id.initialize({
        client_id: this.clientId,
        callback: (response: GoogleCredentialResponse) => {
          onSuccess(response.credential);
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Default button configuration
      const buttonConfig: GoogleButtonConfig = {
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        logo_alignment: 'left',
        ...config,
      };

      // Remove width property if it's '100%' or similar invalid values
      if (buttonConfig.width === '100%' || buttonConfig.width?.includes('%')) {
        delete buttonConfig.width;
      }

      // Render the button
      window.google.accounts.id.renderButton(element, buttonConfig);
      
      console.log('Google Sign-In button rendered successfully');
    } catch (error) {
      console.error('Error rendering Google Sign-In button:', error);
      onError(error instanceof Error ? error : new Error('Failed to render Google Sign-In button'));
    }
  }

  /**
   * Disable auto-select for Google Sign-In
   */
  public disableAutoSelect(): void {
    if (window.google?.accounts?.id) {
      window.google.accounts.id.disableAutoSelect();
    }
  }

  /**
   * Check if Google OAuth is properly configured
   */
  public isConfigured(): boolean {
    // SECURITY: Only return true if we have a valid client ID
    return Boolean(this.clientId && this.clientId.length > 20 && this.clientId.includes('.apps.googleusercontent.com'));
  }

  /**
   * Get the current client ID (for debugging)
   */
  public getClientId(): string {
    return this.clientId;
  }
}

// Export singleton instance
export default GoogleAuthService.getInstance(); 